/**
 * Selector de negocio
 * -----------------------------------------------------------------------------
 * La pantalla de «¿en qué negocio vas a trabajar?» que aparece al entrar cuando
 * una cuenta pertenece a varias empresas. Se repite igual en todas las apps de
 * la suite, así que vive aquí y no en cada una.
 *
 *   CompanyTheme.workspacePicker('#negocio', {
 *     items: [
 *       { value: 1, label: 'Owari',  meta: 'Owari S.A.S. · NIT 901000000-2' },
 *       { value: 2, label: 'Pinkes', meta: 'Pinkes sas · NIT 585851' },
 *     ],
 *     onSubmit: (valor) => (location.href = `/entrar/${valor}`),
 *   });
 *
 * **Por qué un desplegable y no una lista de tarjetas.** Con dos negocios las
 * tarjetas se ven bien; con cuarenta son un scroll donde hay que buscar a ojo.
 * El desplegable del theme trae buscador —y sin tildes—, así que se llega
 * escribiendo tres letras. Y si son cientos, `url` los pide al servidor por
 * páginas en vez de meterlos todos en el HTML.
 *
 * El aspecto no se pierde: cada opción lleva su texto secundario —la razón
 * social y el NIT— y, si se le da, un icono. Se ve como las tarjetas, pero
 * escala.
 *
 * El theme pone la pantalla y el comportamiento; los negocios, los textos y lo
 * que pasa al continuar los pone la aplicación.
 */

import { workspaceES } from './locale-es.js';
import { initMdbOn } from './mdb-init.js';
import { setupRemoteSelects } from './remote-select.js';

let mdbComponents = null;

/** Recibe los componentes de MDB. Lo llama `autoInit()`. */
export function setWorkspaceComponents(components = {}) {
  mdbComponents = components;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

class WorkspacePicker {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    if (!this.container) throw new Error('company-theme: contenedor del selector de negocio no encontrado');

    this.options = options;
    this.items = options.items || [];
    this.id = `workspace-${Date.now()}`;

    this._build();
    this._enhance();
  }

  _build() {
    const o = this.options;

    this.container.innerHTML = '';
    this.container.classList.add('workspace-picker');

    if (o.brand !== false) this.container.appendChild(this._brand());

    this.container.appendChild(el('h1', 'workspace-picker-title', o.title || workspaceES.title));
    this.container.appendChild(el('p', 'workspace-picker-text', o.text || workspaceES.text));

    const panel = el('div', 'page-panel workspace-picker-panel');

    // El select va suelto con su label hermano: `upgradeFloatingLabels` lo
    // convierte al markup que espera MDB, igual que en los formularios.
    this.select = el('select', 'form-select');
    this.select.id = this.id;
    this.select.setAttribute('data-mdb-select-init', '');

    if (o.url) {
      this.select.setAttribute('data-company-remote-url', o.url);
      if (o.pageSize) this.select.setAttribute('data-company-remote-page-size', o.pageSize);
    }

    this.select.appendChild(el('option', null, ''));
    this.items.forEach((item) => this.select.appendChild(this._option(item)));

    const label = el('label', 'form-label', o.label || workspaceES.label);
    label.htmlFor = this.id;

    panel.appendChild(this.select);
    panel.appendChild(label);

    // Ficha del elegido, debajo del campo: confirma que se escogió el correcto
    // antes de entrar. Con nombres parecidos —«Owari» y «Owari Agro»— el NIT es
    // lo único que los distingue.
    this.detail = el('div', 'workspace-picker-detail');
    this.detail.hidden = true;
    panel.appendChild(this.detail);

    this.submit = el('button', 'btn btn-primary w-100 mt-3', o.submitText || workspaceES.submit);
    this.submit.type = 'button';
    this.submit.disabled = true; // no se entra sin elegir
    panel.appendChild(this.submit);

    this.container.appendChild(panel);

    // Sitio para las acciones de la app —cerrar sesión, crear negocio—, que el
    // theme no conoce.
    this.actions = el('div', 'workspace-picker-actions');
    this.container.appendChild(this.actions);
    (o.actions || []).forEach((action) => this.actions.appendChild(this._action(action)));

    this.select.addEventListener('change', () => this._onChange());
    this.submit.addEventListener('click', () => this.submitSelection());
  }

  _brand() {
    const brand = el('span', 'owari-brand workspace-picker-brand');
    brand.appendChild(el('span', 'owari-brand-mark'));
    brand.appendChild(el('span', 'owari-brand-name', 'owari'));
    return brand;
  }

  _option(item) {
    const option = el('option', null, item.label);
    option.value = item.value;

    // MDB pinta el texto secundario y el icono dentro de cada opción: es lo que
    // reproduce el aspecto de las tarjetas sin renunciar al buscador.
    if (item.meta) option.setAttribute('data-mdb-secondary-text', item.meta);
    if (item.icon) option.setAttribute('data-mdb-icon', item.icon);
    if (item.selected) option.selected = true;

    return option;
  }

  _action(action) {
    const node = el(action.href ? 'a' : 'button', `btn ${action.variant || 'btn-outline-primary'}`);
    node.textContent = action.label;

    if (action.href) node.href = action.href;
    else node.type = 'button';

    if (action.onClick) node.addEventListener('click', action.onClick);
    return node;
  }

  _enhance() {
    if (!mdbComponents) {
      document.addEventListener('DOMContentLoaded', () => this._enhance(), { once: true });
      return;
    }

    initMdbOn(this.container, mdbComponents);
    setupRemoteSelects(this.container, mdbComponents);

    this._onChange();
  }

  _onChange() {
    const item = this.selected;

    this.submit.disabled = !item;
    this.detail.hidden = !item || !item.meta;
    if (item && item.meta) this.detail.textContent = item.meta;

    if (!item) return;

    this.container.dispatchEvent(
      new CustomEvent('selected.company.workspace', { bubbles: true, detail: { item } })
    );
  }

  /** El negocio elegido, con lo que la app haya declarado. */
  get selected() {
    const value = this.select.value;
    if (!value) return null;

    const known = this.items.find((item) => String(item.value) === value);
    if (known) return known;

    // Catálogo remoto: el negocio no estaba en `items`, se arma con lo pintado.
    const option = this.select.options[this.select.selectedIndex];
    return {
      value,
      label: option ? option.textContent : value,
      meta: option ? option.getAttribute('data-mdb-secondary-text') : null,
    };
  }

  submitSelection() {
    const item = this.selected;
    if (!item) return;

    this.container.dispatchEvent(
      new CustomEvent('submit.company.workspace', { bubbles: true, detail: { item } })
    );

    if (this.options.onSubmit) this.options.onSubmit(item.value, item);
  }

  dispose() {
    this.container.innerHTML = '';
    this.container.classList.remove('workspace-picker');
    delete this.container.__companyWorkspace;
  }
}

/**
 * @param {string|HTMLElement} container
 * @param {Object} options
 * @returns {WorkspacePicker}
 */
export function workspacePicker(container, options) {
  const picker = new WorkspacePicker(container, options);
  picker.container.__companyWorkspace = picker;
  return picker;
}

export { WorkspacePicker };
